import * as fromDocuments from '../actions/documents.actions';
import { Injector } from '@angular/core';
import { DateService, NotificationInterface } from 'outshared-lib';
import { Bewijsstuk, Bewijsstukken, Knop } from '@app/core/interfaces/bewijsstukken';
import * as fromClearActions from '../actions/clear.actions';

/**
 *  ids equals bewijsstuk_volgnummer
 */
export interface State {
    ids: string[];
    entities: { [id: string]: Bewijsstukken };
    lastUpload: string;
    loading: boolean;
    loaded: { [id: string]: boolean };
    messages: { [id: string]: NotificationInterface[] };
}

const initialState: State = {
    ids: [],
    entities: {},
    lastUpload: null,
    loading: false,
    loaded: {},
    messages: {},
};

/**
 *  Object.assign(target, ...sources);
 *
 *  Properties in the target object will be overwritten by properties in the sources if they have the same key.
 *  Later sources' properties will similarly overwrite earlier ones.
 *
 *  Object.assign({}, currentEntities, newEntities);
 */

export function reducer(state = initialState, action: fromDocuments.Actions): State {
    const injector = Injector.create({ providers: [{ provide: DateService, deps: [] }] });
    const dateService: DateService = injector.get(DateService);
    switch (action.type) {
        case fromDocuments.LOAD: {
            return {
                ...state,
                loading: true,
            };
        }

        case fromDocuments.LOAD_SUCCESS: {
            if (!action.payload) {
                return state;
            }

            const dossier_volgnummer = action.payload.dossier_volgnummer;
            const bewijsstukken = action.payload.bewijsstukken;

            // Create new ids
            const ids = state.ids.filter((id) => id !== dossier_volgnummer);

            // Copy new entity to old entities
            const entity = state.entities[dossier_volgnummer];
            const newEntity = Object.assign({}, entity, bewijsstukken);

            // Loaded true
            const newLoadedState = { [action.payload.dossier_volgnummer]: true };

            // Return new state
            return {
                ...state,
                ids: [...ids, dossier_volgnummer],
                entities: Object.assign({}, state.entities, {
                    [dossier_volgnummer]: newEntity,
                }),
                loading: false,
                loaded: Object.assign({}, state.loaded, newLoadedState),
            };
        }

        case fromDocuments.SUBMIT: {
            return {
                ...state,
                loading: true,
            };
        }

        case fromDocuments.SUBMIT_SUCCESS: {
            if (!action.payload) {
                return state;
            }

            const dossier_volgnummer = action.payload.dossier_volgnummer;

            const bewijsstukken = JSON.parse(
                JSON.stringify([...Object.values(state.entities[dossier_volgnummer].bewijsstuk)])
            );
            const knoppen: Knop[] = [];
            const buttonSubmit = {
                knop_naam: 'indienen_bewijsstuk',
                knop_status: 'verborgen',
            };
            const buttonUploadOther = {
                knop_naam: 'uploaden_overig',
                knop_status: 'verborgen',
            };

            knoppen.push(buttonSubmit, buttonUploadOther);

            bewijsstukken.forEach((bewijsstuk) => {
                if (bewijsstuk.document) {
                    bewijsstuk.document.forEach((document) => {
                        document.knoppen.forEach((buttonDocument) => {
                            buttonDocument.knop_status = 'verborgen';
                        });
                    });
                }

                bewijsstuk.knoppen.forEach((button) => {
                    button.knop_status = 'verborgen';
                });
            });

            const newBewijsstukken: Bewijsstuk[] = bewijsstukken;
            const newEntities = Object.assign(
                {},
                {
                    [dossier_volgnummer]: {
                        bewijsstuk: newBewijsstukken,
                        indienen: {
                            upload_enveloppe_volgnummer:
                                state.entities[dossier_volgnummer].indienen.upload_enveloppe_volgnummer,
                        },
                        knoppen: knoppen,
                    },
                }
            );

            return {
                ...state,
                messages: Object.assign({}, state.messages, {
                    [dossier_volgnummer]: action.payload.messages,
                }),
                entities: newEntities,
                loading: false,
            };
        }

        case fromDocuments.SUBMIT_ERROR: {
            const dossier_volgnummer = action.payload.dossier_volgnummer;
            const message = action.payload.message;

            if (!message) {
                return state;
            }

            return {
                ...state,
                loading: false,
                messages: Object.assign({}, state.messages, {
                    [dossier_volgnummer]: message,
                }),
            };
        }

        case fromDocuments.UPLOAD: {
            return {
                ...state,
                lastUpload: null,
                loading: true,
            };
        }

        case fromDocuments.UPLOAD_SUCCESS: {
            const resultaat = action.payload.resultaat;
            const dossier_volgnummer = action.payload.dossier_volgnummer;
            const bewijsstuk_volgnummer = action.payload.bewijsstuk_volgnummer;
            const bewijsstuk = Object.assign({}, state.entities[dossier_volgnummer].bewijsstuk);

            for (const key in bewijsstuk) {
                if (bewijsstuk.hasOwnProperty(key)) {
                    const b = {
                        ...bewijsstuk[key],
                        document: bewijsstuk[key].document ? [...bewijsstuk[key].document] : bewijsstuk[key].document,
                    };

                    if (b.bewijsstuk_volgnummer === bewijsstuk_volgnummer) {
                        if (!b.document) {
                            b.document = [];
                        }

                        b.document.unshift({
                            cynodocs_document_id: null,
                            knoppen: [
                                {
                                    knop_naam: 'verwijderen_upload',
                                    knop_status: 'zichtbaar',
                                },
                            ],
                            pg_document_bestandsnaam: resultaat.upload.bestandsnaam,
                            pg_document_datum: dateService.nowAsString(),
                            upload_enveloppe_item_volgnummer: resultaat.upload_enveloppe_item_volgnummer,
                            upload_enveloppe_volgnummer: resultaat.upload_enveloppe_volgnummer,
                            status: null,
                        });
                    }
                }
            }

            const newBewijsstuk = Object.values(bewijsstuk);
            const newEntity = Object.assign({}, state.entities[dossier_volgnummer], {
                bewijsstuk: newBewijsstuk,
                indienen: {
                    upload_enveloppe_volgnummer: resultaat.upload_enveloppe_volgnummer,
                },
            });

            return {
                ...state,
                lastUpload: resultaat.upload.bestandsnaam,
                entities: Object.assign({}, state.entities, {
                    [dossier_volgnummer]: newEntity,
                }),
                loading: false,
            };
        }

        case fromDocuments.UPLOAD_ERROR: {
            const messages = action.payload;

            if (!messages.length) {
                return state;
            }

            return {
                ...state,
                loading: false,
                messages: Object.assign({}, state.messages, {
                    messages,
                }),
            };
        }

        case fromDocuments.REMOVE_UPLOAD_SUCCESS: {
            const dossier_volgnummer = action.payload.dossier_volgnummer;
            const bewijsstuk_volgnummer = action.payload.bewijsstuk_volgnummer;
            const bewijsstuk = Object.assign({}, state.entities[dossier_volgnummer].bewijsstuk);

            for (const key in bewijsstuk) {
                if (bewijsstuk.hasOwnProperty(key)) {
                    const b = {
                        ...bewijsstuk[key],
                        document: bewijsstuk[key].document ? [...bewijsstuk[key].document] : bewijsstuk[key].document,
                    };
                    if (b.bewijsstuk_volgnummer === bewijsstuk_volgnummer && b.document) {
                        b.document = b.document.filter(
                            (d) =>
                                d.upload_enveloppe_item_volgnummer !== action.payload.upload_enveloppe_item_volgnummer
                        );
                    }
                }
            }

            const newBewijsstuk = Object.values(bewijsstuk);
            const newEntity = Object.assign({}, state.entities[dossier_volgnummer], {
                bewijsstuk: newBewijsstuk,
            });

            // Return new state
            return {
                ...state,
                entities: Object.assign({}, state.entities, {
                    [dossier_volgnummer]: newEntity,
                }),
                loading: false,
            };
        }

        case fromClearActions.CLEAR_CLAIMS_STORE: {
            return initialState;
        }

        default: {
            return state;
        }
    }
}

export const getIds = (state: State) => state.ids;
export const getEntities = (state: State) => state.entities;
export const isLoading = (state: State) => state.loading;
export const getLoaded = (state: State) => state.loaded;
export const getMessages = (state: State) => state.messages;
export const getLastUpload = (state: State) => state.lastUpload;
